import React, { createContext, useContext, useState, useMemo } from 'react';
import type { AuthContextType, AuthProviderProps, User } from './auth.types';
import { useAuthSynchronization } from '../hooks/useAuthSynchronization';
import { useAuthActions, clearStaleAuthStorage } from '../hooks/useAuthActions';
import { useRoleManagement } from '../hooks/useRoleManagement';

const AuthContext = createContext<AuthContextType | null>(null);

export { AuthContext }; // Export for Storybook mocking

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { refreshUser, loadUserProfile } = useAuthSynchronization(
    user,
    setUser,
    setIsLoading,
    clearStaleAuthStorage
  );

  // Recovery hook for clearing impersonation via query parameter
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('clear_impersonation') === 'true') {
      const clearImpersonation = async () => {
        try {
          const { supabase } = await import('../lib/supabase');
          const { error } = await supabase.rpc('stop_impersonating_user');
          if (!error) {
            // Remove the query param from URL without reloading
            const url = new URL(window.location.href);
            url.searchParams.delete('clear_impersonation');
            window.history.replaceState({}, '', url.pathname + url.search);
            // Refresh user state
            await refreshUser();
          }
        } catch (err) {
          if (import.meta.env.DEV) {
            console.warn('🧹 URL Recovery: Failed to clear admin impersonation', err);
          }
        }
      };
      clearImpersonation();
    }
  }, [refreshUser]);

  const {
    login,
    signup,
    resetPassword,
    logout,
    resendConfirmationEmail,
  } = useAuthActions(setUser, setIsLoading, loadUserProfile);

  const {
    hasPermission,
    hasAnyPermission,
    isDualRole,
    switchRole,
  } = useRoleManagement(user, setUser, loadUserProfile);

  const value = useMemo(() => ({
    user,
    login,
    signup,
    resetPassword,
    logout,
    isLoading,
    hasPermission,
    hasAnyPermission,
    refreshUser,
    switchRole,
    isDualRole,
    resendConfirmationEmail,
  }), [
    user,
    login,
    signup,
    resetPassword,
    logout,
    isLoading,
    hasPermission,
    hasAnyPermission,
    refreshUser,
    switchRole,
    isDualRole,
    resendConfirmationEmail,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
